/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {ErrorBoundary} from '../../notifications/ErrorBoundary';
import {RequiredPropsWithChildren} from '../../types/components';
import ErrorHandler, {ErrorClientProps} from './ErrorHandler';
import useErrorHandler from './useErrorHandler';

export interface ErrorHandlerWrapperProps extends RequiredPropsWithChildren, ErrorClientProps {
};

/**
 * The ErrorHandlerWrapper component is designed to handle errors within a React application.
 * It wraps child components and provides the ability to refresh and retry operations when an
 * error occurs. The component accepts child components and an onRefresh callback function,
 * which is executed when a refresh is requested. It can be used to improve the user experience
 * by gracefully handling errors and providing options for recovery or retrying failed operations.
 *
 *
 * ```typescript
 *
 * // a custom component to be wrapped in the Error handler ...
 *  const Component = (props: Props) => (
 *    <div classes={styles.container}>
 *      <...>
 *    </div>
 *  );
 *
 *  // Use the WrappedComponent in your application.
 *  const App = () => {
 *    return (
 *      <ErrorHandlerWrapper>
 *        <h1>My App</h1>
 *        <MyComponent />
 *      </ErrorHandlerWrapper>
 *    );
 *  };
 *
 *  export default App;
 *
 * ```
 *
 * @param children - The child components to be wrapped.
 * @param onRefresh - A callback function to be executed when a refresh is requested.
 *
 * @returns - A React component that provides error handling capabilities.
 */
export const ErrorHandlerWrapper = ({
  children,
  onRefresh,
  onError,
  omitRefresh = false
}: ErrorHandlerWrapperProps) => {
  const {
    error,
    onRefreshHandler,
    onErrorHandler
  } = useErrorHandler(onRefresh, onError);

  if (!error) {
    try {
      return (
        <ErrorBoundary onRefresh={onRefreshHandler}
          errorText={translate('common:errorHandler.message')}
          onError={onErrorHandler}>
          {children}
        </ErrorBoundary>
      );
    } catch (err) {
      onErrorHandler(err);
    }
  }
  return (
    <div style={{marginTop: '25%'}}>
      <ErrorHandler hasError
        omitRefresh={omitRefresh}
        onRefresh={onRefreshHandler}
        errorText={translate('common:errorHandler.message')} />
    </div>
  );
};

export default ErrorHandlerWrapper;
